import { useState, useEffect } from "react";
import Jira from "../../services/Jira";
import ErrorMessage from "../../components/common/ErrorMessage";
import AccessibilityLevelsChart from "../../components/charts/defects/accessibility/AccessibilityLevelsChart";
import DefectAccessibilitySeverityChart from "../../components/charts/defects/accessibility/DefectAccessibilitySeverityChart";
import DefectsAccessibilityModal from "../../components/common/modals/DefectsAccessibilityModal";
import DefectCountAccessibilityChart from "../../components/charts/defects/defects/DefectCountAccessibilityChart";
import AccessibilityStatusChart from "../../components/charts/defects/accessibility/AccessibilityStatusChart";

export default function DefectsAccessibility({
  token,
  currentProjectId,
  iterations,
}) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentIteration, setCurrentIteration] = useState("Overall");
  const [currentIterationId, setCurrentIterationId] = useState("Overall");
  const [showModal, setShowModal] = useState(false);
  const [levelsIssueMap, setLevelsIssueMap] = useState(null);
  const [severityIssueMap, setSeverityIssueMap] = useState(null);
  const [relatedIssueMap, setRelatedIssueMap] = useState(null);
  const [modalData, setModalData] = useState({
    title: "",
    rows: [],
  });
  const [defectByAccessibilityLevelsMaps, setDefectByAccessibilityLevelsMaps] =
    useState(null);
  const [
    issuesByAccessibilitySeverityMap,
    setIssuesByAccessibilitySeverityMap,
  ] = useState(null);
  const [relatedCurrentMap, setRelatedCurrentMap] = useState(null);

  const handleIterationClick = (event, id) => {
    setCurrentIterationId(id);
    setCurrentIteration(event.target.id);
  };

  async function getAccessibilityLevelsJiraInfo() {
    setLoading(true);
    const data = await Jira.getAccessibilityLevelsJiraInfo(
      token,
      currentProjectId
    );
    if (data?.errMsg) {
      setErrorMessage(data.errMsg);
    } else {
      setDefectByAccessibilityLevelsMaps(
        data.data.defectByAccessibilityLevelsMaps
      );

      setLevelsIssueMap(data.data.issueMap);
    }
    setLoading(false);
  }

  async function getAccessibilitySeverity() {
    setLoading(true);
    const data = await Jira.getAccessibilitySeverity(token, currentProjectId);
    if (data?.errMsg) {
      setErrorMessage(data.errMsg);
    } else {
      setIssuesByAccessibilitySeverityMap(
        data.data.issuesByAccessibilitySeverityMap
      );
      setSeverityIssueMap(data.data.issueMap);
    }
    setLoading(false);
  }

  async function getAccessibilityRelated() {
    setLoading(true);
    const data = await Jira.getAccessibilityDefectRelated(
      token,
      currentProjectId
    );
    if (data?.errMsg) {
      setErrorMessage(data.errMsg);
    } else {
      setRelatedIssueMap(data.issueMap);
      setRelatedCurrentMap(data.currentMap);
    }
    setLoading(false);
  }

  useEffect(() => {
    getAccessibilityLevelsJiraInfo();
    getAccessibilitySeverity();
    getAccessibilityRelated();
  }, []);

  return (
    <div>
      <DefectsAccessibilityModal
        isOpen={showModal}
        setShowModal={setShowModal}
        data={modalData}
      />
      <div id="main_container">
        <div
          id="main_container_1"
          className="bg-light container-fluid pt-3 pb-3"
        >
          {loading && (
            <div className="d-flex justify-content-center bg-transparent ">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
          <ErrorMessage errorMessage={errorMessage} />
          <div id="sprints-container" className="container-fluid pt-3 pb-3">
            <div
              className="btn-group ms-4"
              role="group"
              aria-label="Iterations"
            >
              <div key={0} className={"ms-1"}>
                <button
                  type="button"
                  key={0}
                  id={"Overall"}
                  className={
                    "btn btn-outline-primary " +
                    ("Overall" === currentIteration ? "active" : "")
                  }
                  onClick={(e) => handleIterationClick(e, "Overall")}
                >
                  Overall
                </button>
              </div>
              {iterations
                ? Object.keys(iterations).map((iterationId, index) => {
                    return (
                      <div key={index} className={"ms-1"}>
                        <button
                          type="button"
                          key={index}
                          id={iterations[iterationId]}
                          className={
                            "btn btn-outline-primary " +
                            (iterationId === currentIteration ? "active" : "")
                          }
                          onClick={(e) => handleIterationClick(e, iterationId)}
                        >
                          {iterations[iterationId]}
                        </button>
                      </div>
                    );
                  })
                : iterations.map((iteration, index) => {
                    return (
                      <div key={index} className={"ms-1"}>
                        <button
                          type="button"
                          key={iteration.id}
                          id={iteration.iterationId}
                          className={
                            "btn btn-outline-primary " +
                            (iteration.iterationId === currentIteration
                              ? "active"
                              : "")
                          }
                          onClick={handleIterationClick}
                        >
                          {iteration.iterationName}
                        </button>
                      </div>
                    );
                  })}
            </div>
          </div>

          <div
            className="row ms-3 row-cols-auto justify-content-around"
            style={
              loading
                ? {
                    pointerEvents: "none",
                    opacity: "0.4",
                  }
                : {
                    marginTop: "50px",
                  }
            }
          >
            <div id="accessibilityLevelsChart" className="col col-2 mb-4" />
            <div id="accessibilityLevelsChart" className="col col-8 mb-4">
              <div id="open-closed-defects-root">
                <DefectCountAccessibilityChart
                  currentProjectId={currentProjectId}
                  currentIteration={currentIteration}
                  token={token}
                />
              </div>
            </div>
            <div id="accessibilityLevelsChart" className="col col-2 mb-4" />
            <div id="accessibilityLevelsChart" className="col col-5 mb-4">
              <div id="open-closed-defects-root">
                <AccessibilityLevelsChart
                  setModalData={setModalData}
                  setShowModal={setShowModal}
                  issueMap={levelsIssueMap}
                  currentIteration={currentIteration}
                  defectByAccessibilityLevelsMaps={
                    defectByAccessibilityLevelsMaps
                  }
                />
              </div>
            </div>
            <div id="accessibilityLevelsChart" className="col col-5 mb-4">
              <div id="open-closed-defects-root">
                <DefectAccessibilitySeverityChart
                  setModalData={setModalData}
                  setShowModal={setShowModal}
                  currentIteration={currentIteration}
                  issueMap={severityIssueMap}
                  issuesByAccessibilitySeverityMap={
                    issuesByAccessibilitySeverityMap
                  }
                />
              </div>
            </div>
            <div id="accessibilityLevelsChart" className="col col-5 mb-4">
              <div id="open-closed-defects-root">
                <AccessibilityStatusChart
                  issueMap={relatedIssueMap}
                  currentMap={relatedCurrentMap}
                  currentIteration={currentIterationId}
                  setModalData={setModalData}
                  setShowModal={setShowModal}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
